import React, { useEffect } from 'react'
import {
  Routes,
  Route,
  BrowserRouter as Router,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  Outlet,
} from 'react-router-dom'
import { Auth } from 'aws-amplify'
import { SnackbarProvider } from 'notistack'
import { datadogLogs } from '@datadog/browser-logs'
import { makeStyles } from '@mui/styles'
import { LicenseInfo } from '@mui/x-license'
import CssBaseline from '@mui/material/CssBaseline'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import FeatureFlagProvider from '@/feature_flags/FeatureFlagProvider'
import { isLocalDev } from '@/utils/local-dev'
import { createIDBPersister } from '@/utils/persister'
import AuthProvider, { useAuthentication } from '@/components/auth/AuthProvider'
import DemoModeProvider from '@/components/demo-mode-provider/DemoModeProvider'
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary'
import RecentOrganizationsProvider from '@/components/organizations/RecentOrganizationsProvider'
import RootOrganizationProvider from '@/components/organizations/RootOrganizationProvider'
import { NotificationProvider } from '@/components/notifications/NotificationProvider'
import PageTitle from '@/components/PageTitle'
import Application from './pages/Application'
import ApplicationLite from './pages/ApplicationLite'
import ZerapixThemeProvider from './theme'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})
const persister = createIDBPersister()

!isLocalDev() &&
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
    ],
    environment: import.meta.env.VITE_ENVIRONMENT,

    // We recommend adjusting this value in production, or using tracesSampler
    // For finer control
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
  })

!isLocalDev() &&
  datadogLogs.init({
    clientToken: 'pub1ad8354d55ae38a2103b16352b97ac95',
    site: 'us5.datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: 'pixydocs-web',
    env: import.meta.env.VITE_ENVIRONMENT,
    version: import.meta.env.VITE_VERSION,
  })

LicenseInfo.setLicenseKey(
  'e3e642adfa4c51f5def5ab3afb83d584T1JERVI6MzE4NDQsRVhQSVJZPTE2Njc0Mjc3NjEwMDAsS0VZVkVSU0lPTj0x',
)

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

type RequireAuthProps = {
  children: JSX.Element
}

const useStyles = makeStyles({
  root: {
    bottom: 60,
  },
})

function RequireAuth({ children }: RequireAuthProps) {
  const { authenticated, isLoading } = useAuthentication()
  if (isLoading) return null
  if (!authenticated) Auth.federatedSignIn()
  return authenticated ? children : null
}

export default function Main() {
  const classes = useStyles()

  // Normally react-query takes care of garbage collection, but it turns out there are rare circumstances where it doesn't.
  // This is a fall-back to take care of any inactive queries that are not being garbage collected.
  useEffect(() => {
    setInterval(() => {
      if (queryClient.getQueryCache().getAll().length > 100) {
        queryClient.removeQueries({
          type: 'inactive',
        })
      }
    }, 1000 * 60 * 30) // 30 minutes
  }, [])

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // Control what gets persisted here
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            if (query.state.status !== 'success') return false
            const queryKey = query.queryKey
            const lastElement = queryKey[queryKey.length - 1]
            if (
              lastElement &&
              typeof lastElement === 'object' &&
              'persist' in lastElement
            )
              return !!lastElement.persist
            return false
          },
        },
      }}
    >
      <PageTitle>Pixydocs</PageTitle>
      <AuthProvider>
        <ZerapixThemeProvider>
          <FeatureFlagProvider>
            <DemoModeProvider>
              <CssBaseline />
              <Router>
                <SentryRoutes>
                  <Route
                    path="*"
                    element={
                      <>
                        <SnackbarProvider
                          autoHideDuration={2500}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          classes={{
                            containerRoot: classes.root,
                          }}
                        >
                          <NotificationProvider>
                            <RequireAuth>
                              <ErrorBoundary>
                                <RootOrganizationProvider>
                                  <RecentOrganizationsProvider>
                                    <Outlet />
                                  </RecentOrganizationsProvider>
                                </RootOrganizationProvider>
                              </ErrorBoundary>
                            </RequireAuth>
                          </NotificationProvider>
                        </SnackbarProvider>
                        <ReactQueryDevtools position="right" />
                      </>
                    }
                  >
                    <Route path="lite/*" element={<ApplicationLite />} />
                    <Route path="*" element={<Application />} />
                  </Route>
                </SentryRoutes>
              </Router>
            </DemoModeProvider>
          </FeatureFlagProvider>
        </ZerapixThemeProvider>
      </AuthProvider>
    </PersistQueryClientProvider>
  )
}
